import React, { createContext, useContext, useEffect, useState } from "react";
import { AuthenticationService } from "../../core/authenticationService/AuthenticationService";
import { NotificationsFeature } from "../../domain/notifications/notificationsFeature";
import { AuthUser } from "../../domain/models/AuthUser";

interface AuthContextValue {
  authUser: AuthUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextValue>({
  authUser: null,
  isLoading: true,
  isAuthenticated: false,
});

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [authUser, setAuthUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const authService = AuthenticationService.getInstance();
    const unsubscribe = authService.observeAuthState((user) => {
      setAuthUser(user);
      setIsLoading(false);
      if (user?.id) {
        // Registers the FCM token when permission is already granted, and
        // refreshes it on every app start (web has no token-refresh event).
        NotificationsFeature.startForAuthenticatedUser(user.id).catch(
          () => undefined,
        );
      }
    });

    return () => unsubscribe();
  }, []);

  const value: AuthContextValue = {
    authUser,
    isLoading,
    isAuthenticated: !!authUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
